/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import React from "react"
import PropTypes from "prop-types"
import { DirectionsRenderer, InfoBox } from "@react-google-maps/api"
import theme from "styles/theme"
import DistanceLabel from "../DistanceLabel"
import PolygonInfoLabel from "../PolygonInfoLabel"
import Marker from "../Marker"
import KeyPointInfoLabel from "../KeyPointInfoLabel"
import KeyRoadInfoLabel from "../KeyRoadInfoLabel"
import HighwayInfoLabel from "../HighwayInfoLabel"
import Polygon from "../Polygon"
import Polyline from "../Polyline"

const polygonOptions = {
  strokeColor: theme.color.main03,
  fillColor: theme.color.main03,
  fillOpacity: 0.16,
  strokeWeight: 2,
}
const polyLineOptions = {
  routePolyline: {
    strokeWeight: 3,
    strokeColor: theme.color.quinary03,
    zIndex: 201,
  },
  borderPolyline: {
    strokeWeight: 4,
    strokeColor: theme.color.quinary02,
    zIndex: 200,
  },
}

const MapLocation = ({
  isSelected,
  locationOptions,
  getMarkerLocation,
  setShowSpotlight,
}) => {
  const mapCoordinates = (coordinates) =>
    coordinates.map((coordinate) => ({
      lat: coordinate[1],
      lng: coordinate[0],
    }))

  const locationType = {
    Point: ({
      project: {
        id,
        coordinates,
        markerIcon,
        infoBoxContent,
        isHighlighted,
        onInfoBoxClick,
        onInfoBoxVisibilityChange,
        name,
      },
      clusterer,
      onClickHandler,
      pointType,
      overlay,
    }) => {
      return (
        <Marker
          key={id}
          id={id}
          name={name}
          infoBoxContent={infoBoxContent}
          isSelected={isSelected}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          clusterer={clusterer}
          onClick={onClickHandler}
          pointType={pointType}
          markerIcon={markerIcon}
          onUnmount={(marker) => marker.setMap(null)}
          overlay={overlay}
          isHighlighted={isHighlighted}
          onInfoBoxClick={onInfoBoxClick}
          onInfoBoxVisibilityChange={onInfoBoxVisibilityChange}
          getMarkerLocation={getMarkerLocation}
          setShowSpotlight={setShowSpotlight}
        />
      )
    },
    Polygon: ({
      project: { id, coordinates, innerCoords },
      onPolygonLoadHandler,
      pointType,
      options,
    }) => {
      const mapedCoordinates = mapCoordinates(coordinates[0])
      const innerCoordinates = innerCoords ? mapCoordinates(innerCoords) : []

      return (
        <Polygon
          options={{ ...polygonOptions, ...options }}
          type={pointType}
          id={id}
          paths={[mapedCoordinates, innerCoordinates]}
          onLoad={onPolygonLoadHandler}
        />
      )
    },
    Directions: ({
      project: { id, coordinates },
      onDirectionsLoadHandler,
      polylinetype,
    }) => {
      return (
        <DirectionsRenderer
          options={{
            preserveViewport: true,
            polylineOptions: polyLineOptions[polylinetype],
            markerOptions: {
              opacity: 0,
              icon: {
                url: "",
                scaledSize: new window.google.maps.Size(24, 24),
              },
            },
          }}
          key={id}
          directions={coordinates}
          onLoad={onDirectionsLoadHandler}
        />
      )
    },
    InfoWindow: ({ project: { id, coordinates, duration, distance } }) => {
      return (
        <InfoBox
          key={id}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          closeBoxURL=""
          options={{
            disableAutoPan: true,
          }}
        >
          <DistanceLabel duration={duration} distance={distance} />
        </InfoBox>
      )
    },
    PolygonInfoLabel: ({
      project: { id, coordinates, text, color, subType },
      options,
    }) => {
      const { fillColor } = options || {}
      return (
        <InfoBox
          key={id}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          closeBoxURL=""
          options={{
            pixelOffset:
              subType === "centred"
                ? new window.google.maps.Size(0, 0)
                : new window.google.maps.Size(-16, -16),
            disableAutoPan: true,
          }}
        >
          <PolygonInfoLabel
            text={text}
            color={fillColor || color}
            subType={subType}
          />
        </InfoBox>
      )
    },
    LineString: ({
      project: {
        coordinates,
        strokeColor,
        strokeWeight,
        zIndex,
        strokeOpacity,
        id,
      },
      polylinetype,
      onClickHandler,
    }) => {
      return (
        <Polyline
          path={mapCoordinates(coordinates)}
          options={{
            strokeWeight,
            strokeColor,
            zIndex,
            strokeOpacity,
          }}
          id={id}
          type={polylinetype}
          onPolylineClick={onClickHandler}
        />
      )
    },
    KeyPointInfoBox: ({
      project: { id, coordinates, aproxTime, name, trafficThroughput, colors },
    }) => {
      return (
        <InfoBox
          key={id}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          closeBoxURL=""
          options={{
            disableAutoPan: true,
          }}
        >
          <KeyPointInfoLabel
            aproxTime={aproxTime}
            name={name}
            trafficThroughput={trafficThroughput}
            colors={colors}
          />
        </InfoBox>
      )
    },
    KeyRoadInfoBox: ({
      project: { id, coordinates, aproxTime, name, trafficThroughput, colors },
    }) => {
      return (
        <InfoBox
          key={id}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          closeBoxURL=""
          options={{
            disableAutoPan: true,
          }}
        >
          <KeyRoadInfoLabel
            aproxTime={aproxTime}
            name={name}
            trafficThroughput={trafficThroughput}
            colors={colors}
          />
        </InfoBox>
      )
    },
    HighwayInfoBox: ({
      project: { id, coordinates, aproxTime, name, colors, icon },
    }) => {
      return (
        <InfoBox
          key={id}
          position={{ lat: coordinates[1], lng: coordinates[0] }}
          closeBoxURL=""
          options={{
            disableAutoPan: true,
          }}
        >
          <HighwayInfoLabel
            aproxTime={aproxTime}
            name={name}
            colors={colors}
            icon={icon}
          />
        </InfoBox>
      )
    },
  }
  return locationType[locationOptions.project.type](locationOptions)
}

MapLocation.propTypes = {
  locationOptions: PropTypes.shape({
    projects: PropTypes.shape({}),
    onPolygonLoadHandler: PropTypes.func,
    clusterer: PropTypes.shape({}), // @react-google-maps/api clusterer Type
    onClickHandler: PropTypes.func,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
}
export default MapLocation
